/* import __COLOCATED_TEMPLATE__ from './copilot.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { taskFor } from 'ember-concurrency-ts';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type IntlService from 'ember-intl/services/intl';

interface Args {}

const SALESFORCE_COPILOT_PACKAGE_ID = '04tgK0000006h3tQAA';
const SALESFORCE_GENERIC_INSTALL_URL = `https://login.salesforce.com/packaging/installPackage.apexp?p0=${SALESFORCE_COPILOT_PACKAGE_ID}`;

export default class SalesforceCopilot extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare appService: any;
  @service declare notificationsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;

  @tracked openSection?: string;
  @tracked private isInstallationInProgress = false;
  @tracked private isSettingUpLightningWebSecurity = false;

  get salesforceConfiguration() {
    return this.salesforcePlatform.salesforceConfig;
  }

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get copilotSeatCount() {
    return this.appService.app.humanAdminsWithCopilotSeat.length;
  }

  get canDetectLightningPageInstallation() {
    return (
      this.salesforcePlatform.isConnected &&
      !this.salesforcePlatform.salesforceSetupData.copilot.unableToDetectLightningPageInstallation
    );
  }

  get isLoading() {
    return (
      this.isLoadingSalesforceSetupData ||
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforceCopilotInstallation
    );
  }

  get isLoadingSalesforceSetupData() {
    return this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforceSetupData;
  }

  get isInstalledOnLightningPagesLoading() {
    return !this.isInstallationInProgress && this.isLoading;
  }

  get isInstallationComplete() {
    return (
      this.salesforcePlatform.salesforceSetupData.copilot.isInstalled &&
      !this.isInstallationInProgress
    );
  }

  get hasCustomSettingsFields() {
    return this.salesforcePlatform.salesforceSetupData.copilot.hasCustomSettingsFields;
  }

  get lightningWebSecurityEnabled() {
    return this.salesforcePlatform.salesforceSetupData.copilot.lightningWebSecurityEnabled;
  }

  @action
  async handleVisibilityChange(_event: Event) {
    if (
      !document.hidden &&
      (this.isInstallationInProgress || this.isSettingUpLightningWebSecurity)
    ) {
      await taskFor(
        this.salesforcePlatform.salesforceDataLoader.checkSalesforceCopilotInstallationTask,
      ).perform();

      if (this.salesforcePlatform.salesforceSetupData.copilot.isInstalled) {
        this.isInstallationInProgress = false;
      }
      if (this.salesforcePlatform.salesforceSetupData.copilot.lightningWebSecurityEnabled) {
        this.isSettingUpLightningWebSecurity = false;
      }
    }
  }

  @action setSection(section: string) {
    this.openSection = section;
  }

  @action openInstallLink() {
    this.isInstallationInProgress = true;
    safeWindowOpen(this.installUrl);
  }

  @action openSessionSettingsPage() {
    this.isSettingUpLightningWebSecurity = true;
    safeWindowOpen(
      `${this.salesforceConfiguration!.lightningForceUrl}/lightning/setup/SecuritySession/home`,
    );
  }

  get installUrl() {
    if (this.salesforceConfiguration?.lightningForceUrl) {
      let url = new URL(SALESFORCE_GENERIC_INSTALL_URL);

      return `${this.salesforceConfiguration.lightningForceUrl}${url.pathname}${url.search}`;
    }

    return SALESFORCE_GENERIC_INSTALL_URL;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Salesforce::Copilot': typeof SalesforceCopilot;
  }
}
